/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Provider, ErrorHandler } from '@angular/core';
import { NetworkStatusService } from 'crimson/lib/network-status-service/network-status.service';
import { LoggingService } from 'crimson/lib/logging/logging.service';
import { LocaleService } from 'crimson/lib/locale-service/locale.service';
import { SystemInfoService } from 'crimson/lib/system-info-service/system-info.service';
import { BridgeService } from 'crimson/lib/bridge/bridge.service';
import { DbCacheService } from 'crimson/lib/db/db-cache.service';
import { DbService } from 'crimson/lib/db/db.service';
import { SharedStorageService } from 'shared-storage/lib/shared-storage.service';
import { SingleEventStreamFactoryService } from 'utils/lib/event-stream-factory/event-stream-factory.service';
import { PlatformType } from './enums/platform-type.enum';
import { OAuthService } from './services/o-auth.service';
import { StarfleetServiceDesktop } from './services/starfleet-desktop.service';
import { StarfleetHttpService } from './services/starfleet-http.service';
import { StarfleetConfig } from './starfleet-config';
/* Services */
import { StarfleetService } from './starfleet.service';

export function starfleetServiceCreator(
    config: StarfleetConfig,
    loggingService: LoggingService,
    starfleetHttpService: StarfleetHttpService,
    oAuthService: OAuthService,
    sharedStorageService: SharedStorageService,
    localeService: LocaleService,
    eventStreamFactory: SingleEventStreamFactoryService,
    networkStatusService: NetworkStatusService,
    systemInfoService: SystemInfoService,
    errorHandler: ErrorHandler,
    bridgeService: BridgeService,
    dbService: DbService
) {
    switch (config.platformType) {
        case PlatformType.Browser:
        case PlatformType.Cef:
            // allow adding window origin to redirect url (incase of browser clients)
            config.redirectUrl = config.redirectUrlIsRelative
                ? window.location.origin + config.redirectUrl
                : config.redirectUrl;
            return new StarfleetServiceDesktop(
                config,
                loggingService,
                starfleetHttpService,
                oAuthService,
                sharedStorageService,
                localeService,
                eventStreamFactory,
                networkStatusService,
                systemInfoService,
                errorHandler,
                bridgeService,
                dbService
            );
        default:
            loggingService
                .getLogger('starfleetServiceCreator')
                .error('Cannot create StarfleetService for unknown platform type: ' + config.platformType);
            return undefined;
    }
}

export const StarfleetServiceProvider: Provider = {
    provide: StarfleetService,
    useFactory: starfleetServiceCreator,
    deps: [
        StarfleetConfig,
        LoggingService,
        StarfleetHttpService,
        OAuthService,
        SharedStorageService,
        LocaleService,
        SingleEventStreamFactoryService,
        NetworkStatusService,
        SystemInfoService,
        ErrorHandler,
        BridgeService,
        DbService
    ]
};
